import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import MessageFormatter from './MessageFormatter';
import ChatHistory from './ChatHistory';
import { getChatSessions, getChatSessionMessages, sendChatHelpRequest } from '../../utils/api.service';
import './Chatbot.sass';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // disconnected, connecting, connected, error
  const [sessionId, setSessionId] = useState(null);

  // Chat History State
  const [historySessions, setHistorySessions] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState(false);
  const [viewMode, setViewMode] = useState('active'); // 'active' or 'history'
  const [selectedHistorySession, setSelectedHistorySession] = useState(null);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isHistoryCollapsed, setIsHistoryCollapsed] = useState(false);
  
  // Store active conversation state when viewing history
  const [savedActiveMessages, setSavedActiveMessages] = useState([]);
  const [savedActiveSessionId, setSavedActiveSessionId] = useState(null);
  const [hasNewActiveMessages, setHasNewActiveMessages] = useState(false);
  
  // Help Request State
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpDescription, setHelpDescription] = useState('');
  const [includeHistory, setIncludeHistory] = useState(true);
  const [isSendingHelp, setIsSendingHelp] = useState(false);
  const [helpSent, setHelpSent] = useState(false);
  
  // Smart Error Detection State
  const [showInlineHelp, setShowInlineHelp] = useState(false);
  const [errorCount, setErrorCount] = useState(0);
  const [lastUserMessages, setLastUserMessages] = useState([]);
  
  // Welcome Tooltip Sequence State
  const [showWelcomeTooltip, setShowWelcomeTooltip] = useState(false);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [tooltipDismissed, setTooltipDismissed] = useState(false);
  
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const isRestoringConversationRef = useRef(false);
  const viewModeRef = useRef('active'); // Track view mode for WebSocket handler
  const inlineHelpTimeoutRef = useRef(null);
  const welcomeTooltipTimeoutRef = useRef(null);
  const messageSequenceTimeoutRef = useRef(null);
  
  const MAX_RECONNECT_ATTEMPTS = 5;
  const RECONNECT_DELAY = 3000;

  // Welcome Tooltip Messages Sequence
  const WELCOME_MESSAGES = [
    "Need help? Ask AutoQ!",
    "Hi! I'm AutoQ, your AI assistant 👋",
    "Click here to chat with me!"
  ];

  // Get authentication token and user info from Redux store
  const authToken = useSelector((state) => 
    state.user?.auth?.signInUserSession?.idToken?.jwtToken
  );
  const isLoggedIn = useSelector((state) => state.user?.isLoggedIn);
  const { emailid: emailId, displayname: displayName } = useSelector(
    (state) => state.app?.userInfo || {}
  );

  // WebSocket URL
  const WS_URL = process.env.REACT_APP_WEBSOCKET_ENDPOINT;
  console.log('WS_URL', WS_URL);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Keep viewModeRef in sync with viewMode state
  // This ensures WebSocket handlers always have access to the latest view mode
  useEffect(() => {
    viewModeRef.current = viewMode;
    console.log('[Chatbot] View mode updated to:', viewMode);
  }, [viewMode]);

  // Connect to WebSocket
  const connectWebSocket = () => {
    if (!isLoggedIn || !authToken) {
      console.warn('[Chatbot] Cannot connect: User not logged in or no auth token');
      setConnectionStatus('error');
      return;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log('[Chatbot] WebSocket already connected');
      return;
    }

    try {
      setConnectionStatus('connecting');
      console.log('[Chatbot] Connecting to WebSocket...');

      // Get user name for query parameter
      const userName = displayName || emailId || 'unknown';
      
      // Create WebSocket connection with user_name as query parameter and auth token via Sec-WebSocket-Protocol header
      const wsUrl = `${WS_URL}?user_name=${encodeURIComponent(userName)}`;
      wsRef.current = new WebSocket(wsUrl, authToken);

      wsRef.current.onopen = () => {
        console.log('[Chatbot] WebSocket connected');
        setConnectionStatus('connected');
        reconnectAttemptsRef.current = 0;
        
        // Only show greeting message if we're not restoring a conversation
        // and there are no existing messages
        if (!isRestoringConversationRef.current) {
          setMessages((currentMessages) => {
            if (currentMessages.length === 0) {
              console.log('[Chatbot] Showing greeting message');
              
              // Personalize greeting with user's name
              let userName = displayName;
              if (!userName && emailId) {
                // Extract first name from email if displayName not available
                userName = emailId.split('@')[0].split('.')[0];
                // Capitalize first letter
                userName = userName.charAt(0).toUpperCase() + userName.slice(1);
              }
              
              const greetingText = userName 
                ? `Hello ${userName}! 👋\n\nI'm AutoQ, your TestingHub AI assistant. I can help you with:\n\n• *Create quotes & orders* (new or existing users)\n• *Manage subscriptions* (renewals, auto-renew settings)\n• *Get product offerings* for any country\n• *Handle refunds* (full or partial)\n• *Register hub applications*\n• *Manage API Monetization capacity*\n\nWhat would you like to do today?`
                : `Hello! 👋\n\nI'm AutoQ, your TestingHub AI assistant. I can help you with:\n\n• *Create quotes & orders* (new or existing users)\n• *Manage subscriptions* (renewals, auto-renew settings)\n• *Get product offerings* for any country\n• *Handle refunds* (full or partial)\n• *Register hub applications*\n• *Manage API Monetization capacity*\n\nWhat would you like to do today?`;
              
              const greetingMessage = {
                id: Date.now(),
                text: greetingText,
                sender: 'bot',
                timestamp: new Date(),
              };
              return [greetingMessage];
            }
            console.log('[Chatbot] Preserving existing messages:', currentMessages.length);
            return currentMessages;
          });
        } else {
          console.log('[Chatbot] Restoring conversation - not showing greeting');
          isRestoringConversationRef.current = false; // Reset flag
        }
      };

      wsRef.current.onmessage = (event) => {
        console.log('[Chatbot] Message received:', event.data);
        console.log('[Chatbot] Current view mode (from ref):', viewModeRef.current);
        
        try {
          const data = JSON.parse(event.data);
          
          // Always capture/update session_id if present in response
          // This ensures we always have the correct session_id from the server
          if (data.session_id) {
            console.log('[Chatbot] Session ID captured/updated:', data.session_id);
            setSessionId(data.session_id);
            
            // CRITICAL: If we're viewing history, also update the saved session ID
            // This ensures when we return to active chat, we have the correct session
            if (viewModeRef.current === 'history') {
              console.log('[Chatbot] Updating saved active session ID while viewing history');
              setSavedActiveSessionId(data.session_id);
            }
          }
          
          // Check if we're viewing history - use REF instead of state to avoid stale closure
          if (viewModeRef.current === 'history') {
            console.log('[Chatbot] Received message while viewing history - queuing for active conversation');
            
            // Handle different message types
            if (data.status === 'processing') {
              console.log('[Chatbot] Active conversation is processing (in background)');
              // Don't change isTyping here - user isn't viewing the active conversation
            } else if (data.status === 'complete' && data.response) {
              const newMessage = {
                id: Date.now(),
                text: data.response,
                sender: 'bot',
                timestamp: new Date(),
              };
              // Add to saved active messages
              setSavedActiveMessages((prev) => [...prev, newMessage]);
              setHasNewActiveMessages(true);
              setIsTyping(false); // Clear typing indicator for active conversation
              console.log('[Chatbot] Added response to active conversation (in background)');
            } else if (data.message || data.response) {
              const newMessage = {
                id: Date.now(),
                text: data.message || data.response,
                sender: 'bot',
                timestamp: new Date(),
              };
              setSavedActiveMessages((prev) => [...prev, newMessage]);
              setHasNewActiveMessages(true);
              setIsTyping(false); // Clear typing indicator for active conversation
              console.log('[Chatbot] Added message to active conversation (in background)');
            }
            return; // Don't update current messages display
          }
          
          // Normal message handling when in active mode
          // Handle different message types based on status
          if (data.status === 'processing') {
            // Show typing indicator when server is processing
            setIsTyping(true);
          } else if (data.status === 'complete' && data.response) {
            // Add bot message when processing is complete
            const newMessage = {
              id: Date.now(),
              text: data.response,
              sender: 'bot',
              timestamp: new Date(),
            };
            setMessages((prev) => [...prev, newMessage]);
            setIsTyping(false);
            
            // Detect error responses (Option 3 - Smart Error Detection)
            const responseText = data.response.toLowerCase();
            const errorIndicators = ['error', 'failed', 'unable', 'could not', 'cannot', 'sorry', 'issue'];
            if (errorIndicators.some(indicator => responseText.includes(indicator))) {
              console.log('[Chatbot] Error detected in bot response');
              setErrorCount(prev => {
                const newCount = prev + 1;
                if (newCount >= 2) {
                  showInlineHelpPrompt();
                }
                return newCount;
              });
            }
          } else if (data.type === 'typing') {
            // Handle explicit typing indicator messages
            setIsTyping(data.isTyping);
          } else if (data.message || data.response) {
            // Fallback: handle generic message or response field
            const newMessage = {
              id: Date.now(),
              text: data.message || data.response,
              sender: 'bot',
              timestamp: new Date(),
            };
            setMessages((prev) => [...prev, newMessage]);
            setIsTyping(false);
            
            // Detect error responses (Option 3)
            const messageText = (data.message || data.response || '').toLowerCase();
            const errorIndicators = ['error', 'failed', 'unable', 'could not', 'cannot', 'sorry', 'issue'];
            if (errorIndicators.some(indicator => messageText.includes(indicator))) {
              console.log('[Chatbot] Error detected in bot message');
              setErrorCount(prev => {
                const newCount = prev + 1;
                if (newCount >= 2) {
                  showInlineHelpPrompt();
                }
                return newCount;
              });
            }
          }
        } catch (error) {
          console.error('[Chatbot] Error parsing message:', error);
          // If it's plain text, add it as a message
          const newMessage = {
            id: Date.now(),
            text: event.data,
            sender: 'bot',
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, newMessage]);
          setIsTyping(false);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('[Chatbot] WebSocket error:', error);
        setConnectionStatus('error');
      };

      wsRef.current.onclose = (event) => {
        console.log('[Chatbot] WebSocket closed:', event.code, event.reason);
        setConnectionStatus('disconnected');
        
        // Attempt to reconnect if not a normal closure
        if (event.code !== 1000 && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttemptsRef.current += 1;
          console.log(`[Chatbot] Reconnecting... Attempt ${reconnectAttemptsRef.current}`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, RECONNECT_DELAY);
        }
      };
    } catch (error) {
      console.error('[Chatbot] Error creating WebSocket:', error);
      setConnectionStatus('error');
    }
  };

  // Disconnect WebSocket
  const disconnectWebSocket = () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    
    if (wsRef.current) {
      wsRef.current.close(1000, 'User closed chat');
      wsRef.current = null;
    }
    
    setConnectionStatus('disconnected');
  };

  // Connect when chat is opened (initial open only)
  useEffect(() => {
    if (isOpen && !isMinimized && viewMode === 'active' && connectionStatus === 'disconnected') {
      // Only auto-connect when first opening the chat
      if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
        console.log('[Chatbot] Initial WebSocket connection on chat open');
        connectWebSocket();
      }
    }

    // Cleanup on unmount
    return () => {
      disconnectWebSocket();
    };
  }, [isOpen]);

  // Fetch chat history when chat is opened
  useEffect(() => {
    if (isOpen && isLoggedIn && authToken) {
      fetchChatSessions();
    }
  }, [isOpen, isLoggedIn, authToken]);

  // Welcome Tooltip Sequence - Show on page load
  useEffect(() => {
    // Only show if chat is closed and not dismissed
    if (!isOpen && !tooltipDismissed) {
      // Initial delay before showing first message (2 seconds after page load)
      welcomeTooltipTimeoutRef.current = setTimeout(() => {
        setShowWelcomeTooltip(true);
        setCurrentMessageIndex(0);
      }, 2000);
    }

    return () => {
      if (welcomeTooltipTimeoutRef.current) {
        clearTimeout(welcomeTooltipTimeoutRef.current);
      }
    };
  }, [isOpen, tooltipDismissed]);

  // Cycle through welcome messages
  useEffect(() => {
    if (showWelcomeTooltip && !isOpen) {
      // Show each message for 4 seconds (giving users time to read)
      if (currentMessageIndex < WELCOME_MESSAGES.length - 1) {
        messageSequenceTimeoutRef.current = setTimeout(() => {
          setCurrentMessageIndex((prev) => prev + 1);
        }, 4000);
      } else {
        // Hide tooltip after showing last message for 5 seconds
        messageSequenceTimeoutRef.current = setTimeout(() => {
          setShowWelcomeTooltip(false);
          setTooltipDismissed(true);
        }, 5000);
      }
    }

    return () => {
      if (messageSequenceTimeoutRef.current) {
        clearTimeout(messageSequenceTimeoutRef.current);
      }
    };
  }, [showWelcomeTooltip, currentMessageIndex, isOpen]);

  // Handle sending messages
  const handleSendMessage = () => {
    if (!inputMessage.trim()) return;
    
    // Don't allow sending messages while viewing history
    if (viewMode === 'history') {
      console.warn('[Chatbot] Cannot send message: Viewing history mode');
      return;
    }
    
    if (connectionStatus !== 'connected') {
      console.warn('[Chatbot] Cannot send message: WebSocket not connected');
      return;
    }

    // Detect frustration in user message (Option 3 - Smart Error Detection)
    if (detectFrustration(inputMessage)) {
      console.log('[Chatbot] Frustration detected in message:', inputMessage);
      setErrorCount(prev => prev + 1);
      
      // Show inline help after 2 frustration messages
      if (errorCount >= 1) {
        showInlineHelpPrompt();
      }
    }

    // Track last few user messages to detect patterns
    setLastUserMessages(prev => [...prev.slice(-4), inputMessage]);

    // Add user message to UI
    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);

    // Show typing indicator immediately for better UX
    setIsTyping(true);

    // Send message to WebSocket server
    try {
      // Include session_id in payload if available
      const payload = {
        message: inputMessage
      };
      
      if (sessionId) {
        payload.session_id = sessionId;
        console.log('[Chatbot] Sending message with session_id:', sessionId);
      }
      
      wsRef.current.send(JSON.stringify(payload));
      console.log('[Chatbot] Message sent:', JSON.stringify(payload));
    } catch (error) {
      console.error('[Chatbot] Error sending message:', error);
      // Clear typing indicator if send fails
      setIsTyping(false);
      
      // Show inline help on send error (Option 3)
      setErrorCount(prev => prev + 1);
      showInlineHelpPrompt();
    }

    // Clear input
    setInputMessage('');
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Toggle chat window
  const toggleChat = () => {
    setIsOpen(!isOpen);
    setIsMinimized(false);
    // Dismiss welcome tooltip when chat is opened
    if (showWelcomeTooltip) {
      setShowWelcomeTooltip(false);
      setTooltipDismissed(true);
    }
  };

  // Dismiss welcome tooltip
  const handleDismissWelcomeTooltip = () => {
    setShowWelcomeTooltip(false);
    setTooltipDismissed(true);
    if (welcomeTooltipTimeoutRef.current) {
      clearTimeout(welcomeTooltipTimeoutRef.current);
    }
    if (messageSequenceTimeoutRef.current) {
      clearTimeout(messageSequenceTimeoutRef.current);
    }
  };

  // Minimize chat window
  const minimizeChat = () => {
    setIsMinimized(!isMinimized);
  };

  // Close chat window
  const closeChat = () => {
    setIsOpen(false);
    disconnectWebSocket();
    // Clear session_id when chat is closed
    setSessionId(null);
    // Reset view mode and clear selected history session
    setViewMode('active');
    setSelectedHistorySession(null);
    // Clear messages
    setMessages([]);
  };

  // Format timestamp
  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Detect frustration keywords in user messages
  const detectFrustration = (message) => {
    const frustrationKeywords = [
      'not working',
      "doesn't work",
      "isn't working",
      'help',
      'error',
      'issue',
      'problem',
      'broken',
      'failed',
      'wrong',
      "can't",
      'unable',
      'fix',
      'stuck',
      'confused',
    ];
    
    const lowerMessage = message.toLowerCase();
    return frustrationKeywords.some(keyword => lowerMessage.includes(keyword));
  };

  // Show inline help prompt temporarily
  const showInlineHelpPrompt = () => {
    setShowInlineHelp(true);
    
    // Clear existing timeout
    if (inlineHelpTimeoutRef.current) {
      clearTimeout(inlineHelpTimeoutRef.current);
    }
    
    // Auto-hide after 15 seconds
    inlineHelpTimeoutRef.current = setTimeout(() => {
      setShowInlineHelp(false);
    }, 15000);
  };

  // Handle opening help modal
  const handleOpenHelpModal = () => {
    setShowHelpModal(true);
    setShowInlineHelp(false); // Hide inline prompt if shown
    setHelpDescription('');
    setIncludeHistory(true);
    setHelpSent(false);
  };

  // Handle closing help modal
  const handleCloseHelpModal = () => {
    setShowHelpModal(false);
    setHelpDescription('');
    setHelpSent(false);
  };

  // Send help request to team
  const handleSendHelpRequest = async () => {
    setIsSendingHelp(true);

    try {
      // Get the correct session ID based on current view mode
      const currentSessionId = viewMode === 'history' 
        ? selectedHistorySession?.session_id 
        : sessionId;

      console.log('[Chatbot] Preparing help request for view mode:', viewMode);
      console.log('[Chatbot] Using session ID:', currentSessionId);

      // Prepare help request data
      const helpData = {
        user: {
          email: emailId,
          name: displayName,
        },
        sessionId: currentSessionId,
        timestamp: new Date().toISOString(),
        description: helpDescription || 'User requested help (no description provided)',
        conversationContext: includeHistory
          ? messages.slice(-10).map(msg => ({
              sender: msg.sender,
              text: msg.text,
              timestamp: msg.timestamp,
            }))
          : [],
      };

      console.log('[Chatbot] Sending help request:', helpData);

      // Send help request to backend
      const response = await sendChatHelpRequest(helpData);
      console.log('[Chatbot] Help request sent successfully:', response);

      setHelpSent(true);
      setErrorCount(0); // Reset error count after help request

      // Don't auto-close - let user close manually or click Slack button
    } catch (error) {
      console.error('[Chatbot] Error sending help request:', error);
      alert('Failed to send help request. Please try again or contact support directly.');
    } finally {
      setIsSendingHelp(false);
    }
  };

  // Toggle history sidebar collapse
  const toggleHistoryCollapse = () => {
    setIsHistoryCollapsed(!isHistoryCollapsed);
  };

  // Fetch chat history sessions
  const fetchChatSessions = async () => {
    if (!isLoggedIn || !authToken) {
      console.warn('[Chatbot] Cannot fetch sessions: User not logged in');
      return;
    }

    setIsLoadingHistory(true);
    setHistoryError(false);

    try {
      const response = await getChatSessions();
      console.log('[Chatbot] Chat sessions fetched:', response.data);
      
      // Ensure we have a valid response structure
      if (response && response.data && Array.isArray(response.data.sessions)) {
        setHistorySessions(response.data.sessions);
      } else {
        console.warn('[Chatbot] Invalid response format for sessions');
        setHistorySessions([]);
      }
    } catch (error) {
      console.error('[Chatbot] Error fetching chat sessions:', error);
      setHistoryError(true);
      setHistorySessions([]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Fetch messages for a specific session
  const fetchSessionMessages = async (sessionIdToFetch) => {
    if (!isLoggedIn || !authToken) {
      console.warn('[Chatbot] Cannot fetch messages: User not logged in');
      return;
    }

    setIsLoadingMessages(true);

    try {
      const response = await getChatSessionMessages(sessionIdToFetch);
      console.log('[Chatbot] Session messages fetched:', response.data);
      
      // Validate response structure
      if (response && response.data && Array.isArray(response.data.messages)) {
        // Convert API response format to internal message format
        const formattedMessages = response.data.messages.map((msg, index) => ({
          id: `${sessionIdToFetch}-${index}`,
          text: msg.content || '',
          sender: msg.role === 'user' ? 'user' : 'bot',
          timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
        }));

        setMessages(formattedMessages);
      } else {
        console.warn('[Chatbot] Invalid response format for messages');
        setMessages([{
          id: Date.now(),
          text: 'Invalid conversation data received.',
          sender: 'bot',
          timestamp: new Date(),
        }]);
      }
    } catch (error) {
      console.error('[Chatbot] Error fetching session messages:', error);
      // Show error message in chat
      setMessages([{
        id: Date.now(),
        text: 'Failed to load conversation history. Please try again.',
        sender: 'bot',
        timestamp: new Date(),
      }]);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  // Handle session selection from history
  const handleSessionSelect = (session) => {
    console.log('[Chatbot] ===== VIEWING HISTORY SESSION =====');
    console.log('[Chatbot] History session selected:', session.session_id);
    console.log('[Chatbot] Current view mode:', viewMode);
    console.log('[Chatbot] Current messages count:', messages.length);
    console.log('[Chatbot] Current session ID:', sessionId);
    
    // Save current active conversation if it exists
    if (viewMode === 'active' && messages.length > 0) {
      const messagesToSave = [...messages];
      const sessionToSave = sessionId;
      
      console.log('[Chatbot] SAVING active conversation:');
      console.log('[Chatbot] - Messages to save:', messagesToSave.length);
      console.log('[Chatbot] - Session ID to save:', sessionToSave);
      console.log('[Chatbot] - First message:', messagesToSave[0]?.text);
      console.log('[Chatbot] - Last message:', messagesToSave[messagesToSave.length - 1]?.text);
      
      setSavedActiveMessages(messagesToSave);
      setSavedActiveSessionId(sessionToSave);
    } else {
      console.log('[Chatbot] NOT saving - viewMode:', viewMode, ', messages.length:', messages.length);
    }
    
    // Reset new messages indicator when switching to history view
    setHasNewActiveMessages(false);
    
    // DON'T disconnect WebSocket - keep the active connection alive
    // This ensures any pending responses for the active session are still received
    console.log('[Chatbot] Keeping WebSocket connected while viewing history');
    
    // Switch to history view mode
    setViewMode('history');
    setSelectedHistorySession(session);
    
    // Small delay to ensure save completes before fetching new messages
    setTimeout(() => {
      console.log('[Chatbot] Fetching history messages for session:', session.session_id);
      fetchSessionMessages(session.session_id);
    }, 50);
  };

  // Return to active/current conversation
  const handleReturnToActive = () => {
    console.log('[Chatbot] ===== RETURNING TO ACTIVE CONVERSATION =====');
    console.log('[Chatbot] Saved messages count:', savedActiveMessages.length);
    console.log('[Chatbot] Saved session ID:', savedActiveSessionId);
    console.log('[Chatbot] First saved message:', savedActiveMessages[0]?.text);
    console.log('[Chatbot] Last saved message:', savedActiveMessages[savedActiveMessages.length - 1]?.text);
    
    // Restore saved active conversation
    const messagesToRestore = [...savedActiveMessages];
    console.log('[Chatbot] Restoring messages:', messagesToRestore.length);
    
    setMessages(messagesToRestore);
    setSessionId(savedActiveSessionId);
    setSelectedHistorySession(null);
    setHasNewActiveMessages(false); // Reset new messages indicator
    
    // Switch back to active mode
    setViewMode('active');
    
    // No need to reconnect - WebSocket is still connected!
    console.log('[Chatbot] Returned to active conversation (WebSocket still connected)');
  };

  // Handle new chat
  const handleNewChat = () => {
    console.log('[Chatbot] Starting new chat');
    
    // Reset typing indicator and input message
    setIsTyping(false);
    setInputMessage('');
    
    // If we're in history mode, we need to disconnect and reconnect
    // If we're in active mode, we just reset the conversation
    if (viewMode === 'history') {
      console.log('[Chatbot] In history mode - will reconnect to start fresh chat');
      
      // Disconnect any existing connection first
      disconnectWebSocket();
      
      // Clear current messages and session
      setMessages([]);
      setSessionId(null);
      setSelectedHistorySession(null);
      
      // Clear saved active conversation since we're starting fresh
      setSavedActiveMessages([]);
      setSavedActiveSessionId(null);
      setHasNewActiveMessages(false);
      
      // Reset the restoring flag
      isRestoringConversationRef.current = false;
      
      // Switch to active mode
      setViewMode('active');
      
      // Refresh history to show the completed session
      console.log('[Chatbot] Refreshing chat history');
      fetchChatSessions();
      
      // Use a small delay to ensure disconnect completes before reconnecting
      setTimeout(() => {
        console.log('[Chatbot] Initiating new WebSocket connection');
        connectWebSocket();
      }, 200);
    } else {
      console.log('[Chatbot] In active mode - starting new conversation (keeping connection)');
      
      // Clear the conversation
      setSessionId(null);
      
      // Clear saved states
      setSavedActiveMessages([]);
      setSavedActiveSessionId(null);
      setHasNewActiveMessages(false);
      
      // Show greeting message for new chat
      const userName = displayName || (emailId ? emailId.split('@')[0].split('.')[0].charAt(0).toUpperCase() + emailId.split('@')[0].split('.')[0].slice(1) : null);
      const greetingText = userName 
        ? `Hello ${userName}! 👋\n\nI'm AutoQ, your TestingHub AI assistant. I can help you with:\n\n• *Create quotes & orders* (new or existing users)\n• *Manage subscriptions* (renewals, auto-renew settings)\n• *Get product offerings* for any country\n• *Handle refunds* (full or partial)\n• *Register hub applications*\n• *Manage API Monetization capacity*\n\nWhat would you like to do today?`
        : `Hello! 👋\n\nI'm AutoQ, your TestingHub AI assistant. I can help you with:\n\n• *Create quotes & orders* (new or existing users)\n• *Manage subscriptions* (renewals, auto-renew settings)\n• *Get product offerings* for any country\n• *Handle refunds* (full or partial)\n• *Register hub applications*\n• *Manage API Monetization capacity*\n\nWhat would you like to do today?`;
      
      const greetingMessage = {
        id: Date.now(),
        text: greetingText,
        sender: 'bot',
        timestamp: new Date(),
      };
      
      setMessages([greetingMessage]);
      
      // Refresh history
      console.log('[Chatbot] Refreshing chat history');
      fetchChatSessions();
    }
  };

  // Check if chatbot is enabled via environment variable
  const isChatbotEnabled = process.env.REACT_APP_ENABLE_CHATBOT === 'true';
  
  // Don't render if chatbot is disabled
  if (!isChatbotEnabled) {
    return null;
  }

  return (
    <div className="chatbot-container">
      {/* Chat Toggle Button */}
      {!isOpen && (
        <div className="chatbot-toggle-wrapper">
          {/* Welcome Tooltip Sequence */}
          {showWelcomeTooltip && (
            <div className="welcome-tooltip-bubble">
              <div className="welcome-tooltip-content">
                <span className="welcome-tooltip-text">
                  {WELCOME_MESSAGES[currentMessageIndex]}
                </span>
                <button
                  className="welcome-tooltip-close"
                  onClick={handleDismissWelcomeTooltip}
                  aria-label="Dismiss"
                >
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                    <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
              {/* Tooltip Arrow */}
              <div className="welcome-tooltip-arrow"></div>
            </div>
          )}

          <button
            className="chatbot-toggle-button"
            onClick={toggleChat}
            aria-label="Open AutoQ chat"
          >
            <svg
              width="60"
              height="60"
              viewBox="0 0 60 60"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Subtle background glow */}
              <circle cx="30" cy="30" r="28" fill="rgba(255, 255, 255, 0.05)" />
              
              {/* AutoQ Text */}
              <text
                x="30"
                y="36"
                fontFamily="Arial, sans-serif"
                fontSize="16"
                fontWeight="bold"
                fill="#FFD100"
                textAnchor="middle"
                letterSpacing="0.5"
              >
                AutoQ
              </text>
              
              {/* Lightning bolt accent above text */}
              <path
                d="M32 15 L28 22 H31 L28 28"
                stroke="#FFD100"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
                opacity="0.9"
              />
              
              {/* Subtle sparkles */}
              <circle cx="20" cy="18" r="1.5" fill="#FFD100" opacity="0.6" />
              <circle cx="40" cy="20" r="1" fill="#FFD100" opacity="0.5" />
            </svg>
          </button>
        </div>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className={`chatbot-window ${isMinimized ? 'minimized' : ''}`}>
          {/* Header */}
          <div className="chatbot-header">
            <h4 className="chatbot-title">AutoQ</h4>
            <div className="chatbot-actions">
              {/* Connection Status Indicator - Only show in active mode */}
              {viewMode === 'active' && (
                <div className={`connection-status ${connectionStatus}`}>
                  <span className="status-dot"></span>
                </div>
              )}
              
              {/* History Mode Indicator */}
              {viewMode === 'history' && (
                <div className="history-mode-badge">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M8 4V8L11 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" fill="none" />
                  </svg>
                  <span>History</span>
                </div>
              )}
              
              {/* Get Help Button - Option 1 */}
              <button
                className="chatbot-help-button"
                onClick={handleOpenHelpModal}
                aria-label="Get help from team"
                title="Need help? Contact our team"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2" fill="none" />
                  <path d="M12 16V16.01M12 8C10.3431 8 9 9.34315 9 11H12V13" stroke="white" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <span>Help</span>
              </button>
              
              {/* Minimize Button */}
              <button
                className="chatbot-action-button"
                onClick={minimizeChat}
                aria-label={isMinimized ? 'Maximize' : 'Minimize'}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M2 8H14" stroke="white" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>

              {/* Close Button */}
              <button
                className="chatbot-action-button"
                onClick={closeChat}
                aria-label="Close chat"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M12 4L4 12M4 4L12 12"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Main Content Area with Sidebar */}
          {!isMinimized && (
            <div className="chatbot-content">
              {/* Chat History Sidebar */}
              {!isHistoryCollapsed && (
                <ChatHistory
                  sessions={historySessions}
                  currentSessionId={viewMode === 'history' ? selectedHistorySession?.session_id : sessionId}
                  onSessionSelect={handleSessionSelect}
                  onNewChat={handleNewChat}
                  onReturnToActive={handleReturnToActive}
                  hasActiveConversation={viewMode === 'history' && savedActiveMessages.length > 0}
                  hasNewActiveMessages={hasNewActiveMessages}
                  onRetry={fetchChatSessions}
                  isLoading={isLoadingHistory}
                  error={historyError}
                />
              )}

              {/* Collapse/Expand Toggle Button */}
              <button 
                className={`history-toggle-button ${isHistoryCollapsed ? 'collapsed' : ''}`}
                onClick={toggleHistoryCollapse}
                aria-label={isHistoryCollapsed ? 'Show chat history' : 'Hide chat history'}
                title={isHistoryCollapsed ? 'Show chat history' : 'Hide chat history'}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  {isHistoryCollapsed ? (
                    // Right chevron (expand)
                    <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  ) : (
                    // Left chevron (collapse)
                    <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  )}
                </svg>
              </button>

              {/* Messages Area */}
              <div className={`chatbot-main ${isHistoryCollapsed ? 'full-width' : ''}`}>
                <div className="chatbot-messages">
                  {/* Loading state for fetching historical messages */}
                  {isLoadingMessages && (
                    <div className="chatbot-empty-state">
                      <div className="loading-spinner-small"></div>
                      <p>Loading messages...</p>
                    </div>
                  )}

                  {/* Empty state */}
                  {!isLoadingMessages && messages.length === 0 && viewMode === 'active' && (
                    <div className="chatbot-empty-state">
                      {connectionStatus === 'connecting' && <p>Connecting to AutoQ...</p>}
                      {connectionStatus === 'connected' && <p>Connected! Start a conversation below.</p>}
                      {connectionStatus === 'disconnected' && (
                        <>
                          <p>Connecting...</p>
                          <p style={{fontSize: '12px', marginTop: '8px', color: '#999'}}>
                            Establishing connection to chat service
                          </p>
                        </>
                      )}
                      {connectionStatus === 'error' && (
                        <>
                          <p>Connection error</p>
                          <p style={{fontSize: '12px', marginTop: '8px', color: '#999'}}>
                            Please check your connection and try again
                          </p>
                        </>
                      )}
                    </div>
                  )}

                  {!isLoadingMessages && messages.length === 0 && viewMode === 'history' && (
                    <div className="chatbot-empty-state">
                      <p>No messages in this conversation</p>
                    </div>
                  )}

                  {/* Messages */}
                  {!isLoadingMessages && messages.length > 0 && (
                    messages.map((message) => (
                      <div
                        key={message.id}
                        className={`chatbot-message ${message.sender}`}
                      >
                        <div className="message-content">
                          <MessageFormatter text={message.text} />
                          <span className="message-timestamp">
                            {formatTime(message.timestamp)}
                          </span>
                        </div>
                      </div>
                    ))
                  )}

                  {/* Typing Indicator - Only in active mode */}
                  {isTyping && viewMode === 'active' && (
                    <div className="chatbot-message bot">
                      <div className="message-content">
                        <div className="typing-indicator">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* Inline Help Prompt - Option 3 */}
                {showInlineHelp && viewMode === 'active' && (
                  <div className="inline-help-prompt">
                    <div className="inline-help-content">
                      <div className="inline-help-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V11H13V17ZM13 9H11V7H13V9Z" fill="currentColor" />
                        </svg>
                      </div>
                      <div className="inline-help-text">
                        <strong>Having trouble?</strong>
                        <p>Our team can help you directly!</p>
                      </div>
                      <button 
                        className="inline-help-button"
                        onClick={handleOpenHelpModal}
                      >
                        Get Help
                      </button>
                      <button 
                        className="inline-help-close"
                        onClick={() => setShowInlineHelp(false)}
                        aria-label="Dismiss"
                      >
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                          <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                {/* Input Area */}
                <div className="chatbot-input-area">
                  {/* History mode notice */}
                  {viewMode === 'history' && (
                    <div className="connection-message history-notice">
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                        <path d="M8 4V8L11 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" fill="none" />
                      </svg>
                      <span>Viewing chat history (read-only)</span>
                    </div>
                  )}

                  {/* Connection status - only in active mode */}
                  {viewMode === 'active' && connectionStatus !== 'connected' && (
                    <div className="connection-message">
                      {connectionStatus === 'connecting' && 'Connecting...'}
                      {connectionStatus === 'disconnected' && 'Disconnected'}
                      {connectionStatus === 'error' && 'Connection error'}
                    </div>
                  )}

                  {/* Input wrapper */}
                  <div className="chatbot-input-wrapper">
                    <input
                      type="text"
                      className="chatbot-input"
                      placeholder={
                        isTyping
                          ? 'Waiting for response...'
                          : viewMode === 'history'
                            ? 'Select "New Chat" to start messaging'
                            : 'Type your message...'
                      }
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      disabled={viewMode === 'history' || connectionStatus !== 'connected' || isTyping}
                    />
                    <button
                      className="chatbot-send-button"
                      onClick={handleSendMessage}
                      disabled={viewMode === 'history' || !inputMessage.trim() || connectionStatus !== 'connected' || isTyping}
                      title={
                        isTyping
                          ? 'Please wait for the response...'
                          : viewMode === 'history'
                            ? 'Cannot send in history mode'
                            : connectionStatus !== 'connected'
                              ? 'Not connected'
                              : !inputMessage.trim()
                                ? 'Type a message first'
                                : 'Send message'
                      }
                      aria-label="Send message"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M2 10L18 2L10 18L8.5 11.5L2 10Z"
                          fill="currentColor"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Help Request Modal */}
      {showHelpModal && (
        <div className="help-modal-overlay" onClick={handleCloseHelpModal}>
          <div className="help-modal" onClick={(e) => e.stopPropagation()}>
            <div className="help-modal-header">
              <h3>Get Help from Our Team</h3>
              <button 
                className="help-modal-close"
                onClick={handleCloseHelpModal}
                aria-label="Close"
              >
                <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
                  <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            {!helpSent ? (
              <>
                <div className="help-modal-body">
                  <p className="help-modal-intro">
                    Having trouble with the MCP tool? Our TestingHub team is here to help!
                    We'll review your conversation and reach out to you at <strong>{emailId}</strong>.
                  </p>

                  <div className="help-modal-field">
                    <label htmlFor="help-description">
                      Describe your issue (optional)
                    </label>
                    <textarea
                      id="help-description"
                      className="help-modal-textarea"
                      placeholder="Tell us what's not working or what you need help with..."
                      value={helpDescription}
                      onChange={(e) => setHelpDescription(e.target.value)}
                      rows={4}
                      disabled={isSendingHelp}
                    />
                  </div>

                  <div className="help-modal-checkbox">
                    <input
                      type="checkbox"
                      id="include-history"
                      checked={includeHistory}
                      onChange={(e) => setIncludeHistory(e.target.checked)}
                      disabled={isSendingHelp}
                    />
                    <label htmlFor="include-history">
                      Include my recent conversation history (last 10 messages)
                    </label>
                  </div>

                  <div className="help-modal-info">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />
                      <path d="M12 16V12M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <span>
                      Your request will be sent to our team's Slack channel. 
                      We'll respond via email as soon as possible.
                    </span>
                  </div>
                </div>

                <div className="help-modal-footer">
                  <button
                    className="help-modal-button help-modal-button-cancel"
                    onClick={handleCloseHelpModal}
                    disabled={isSendingHelp}
                  >
                    Cancel
                  </button>
                  <button
                    className="help-modal-button help-modal-button-send"
                    onClick={handleSendHelpRequest}
                    disabled={isSendingHelp}
                  >
                    {isSendingHelp ? (
                      <>
                        <span className="help-modal-spinner"></span>
                        Sending...
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Send to Team
                      </>
                    )}
                  </button>
                </div>
              </>
            ) : (
              <div className="help-modal-success">
                <div className="help-success-icon">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />
                    <path d="M8 12L11 15L16 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <h4>Help Request Sent!</h4>
                <p>
                  Our TestingHub team has received your request and will reach out to you 
                  at <strong>{emailId}</strong> soon.
                </p>
                <p className="help-success-tip">
                  You can continue using the chat. We'll contact you shortly!
                </p>
                
                <div className="help-success-actions">
                  <button
                    className="view-slack-button"
                    onClick={() => {
                      const slackUrl = process.env.REACT_APP_SLACK_CHANNEL_URL;
                      console.log('[Chatbot] Opening Slack channel:', slackUrl);
                      if (slackUrl) {
                        window.open(slackUrl, '_blank');
                      } else {
                        console.error('[Chatbot] REACT_APP_SLACK_CHANNEL_URL not configured');
                        alert('Slack channel URL is not configured. Please contact your administrator.');
                      }
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M14.5 10c-.83 0-1.5-.67-1.5-1.5v-5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5v5c0 .83-.67 1.5-1.5 1.5zm0 1c.83 0 1.5.67 1.5 1.5v5c0 .83-.67 1.5-1.5 1.5s-1.5-.67-1.5-1.5v-5c0-.83.67-1.5 1.5-1.5zm-5 4c0 .83-.67 1.5-1.5 1.5h-5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5h5c.83 0 1.5.67 1.5 1.5zm1 0c0-.83.67-1.5 1.5-1.5h5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-5c-.83 0-1.5-.67-1.5-1.5z" fill="currentColor"/>
                    </svg>
                    View in Slack Channel
                  </button>
                  
                  <button
                    className="help-success-done-button"
                    onClick={handleCloseHelpModal}
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Chatbot;

